import { environment } from 'environments/environment';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, forkJoin } from 'rxjs';
import { EntityType } from '../models/entityType-model';
import { Entity } from '../models/entity-model';

@Injectable({
    providedIn: 'root'
})
export class EntityTypesService {

    private API_ENTITY_TYPE_URL = environment.ipserver + 'AnA-backend/private/entitytype';

    private API_APP_TO_ENTITY_TYPE_ASSOCIATED_URL = environment.ipserver
        + 'AnA-backend/private/entitytype/findTypeEnityApplicationAssociated';

    private API_APP_TO_ENTITY_TYPE_NOT_ASSOCIATED_URL = environment.ipserver
        + 'AnA-backend/private/entitytype/findTypeEnityApplicationNoAssociated';

    private API_APP_TO_ENTITY_TYPE_REMOVE_URL = environment.ipserver
        + 'AnA-backend/private/entitytype/removeEntityTypeApplication';

    private API_APP_TO_ENTITY_TYPE_ADD_URL = environment.ipserver
        + 'AnA-backend/private/entitytype/addEntityTypeApplication';

    constructor(private http: HttpClient) { }

    /**
     * Method that obtains a list of entity types by calling applicatios rest service
     * @author Castor Transformación Digital - Fabio Oquendo
     * @since 2020-01-27
     * @return Observable with a json that contains a list of entity types
     */
    public getAllEntityTypes(): Observable<any> {
        return this.http.get<any>(`${this.API_ENTITY_TYPE_URL}/findAll`);
    }

    /**
     * Method that obtains a list of entity types by calling applicatios rest service
     * @author Castor Transformación Digital - Fabio Oquendo
     * @since 2020-01-27
     * @return Observable with a json that contains a list of entity types
     */
    public getAllEntityTypesByUser(user: string): Observable<any> {
        const type = 'text' as 'json';
        return this.http.post<any>(`${this.API_ENTITY_TYPE_URL}/findAllUser`, user, { responseType: type });
    }

    /**
     * Method that obtains a entity type by calling applicatios rest service
     * @author Castor Transformación Digital - Fabio Oquendo
     * @since 2020-01-27
     * @param id: The entity type id
     * @return Observable with a json that contains a list of entity types
     */
    public getEntityType(id: number): Observable<Array<EntityType>> {
        const httpHeaders = new HttpHeaders();
		httpHeaders.set('Content-Type', 'application/json');

        return this.http.post<Array<EntityType>>(`${this.API_ENTITY_TYPE_URL}/findById`, id, { headers: httpHeaders });
    }

    /**
     * Method that obtains a list of entity types by calling applicatios rest service
     * @author Castor Transformación Digital - Fabio Oquendo
     * @since 2020-01-27
     * @param entity type: The entity type
     * @return Observable with a json that contains a list of entity types
     */
    public addEntityType(entityType: EntityType): Observable<any> {
        const httpHeaders = new HttpHeaders();
        const type = 'text' as 'json';
        httpHeaders.set('Content-Type', 'application/json');
        return this.http.post<any>(`${this.API_ENTITY_TYPE_URL}/addEntityType`, entityType, { headers: httpHeaders, responseType: type });
    }

    /**
     * Method that obtains a list of entity types by calling applicatios rest service
     * @author Castor Transformación Digital - Fabio Oquendo
     * @since 2020-01-27
     * @param id: The entity type id
     * @return Observable with a json that contains a list of entity types
     */
    public removeEntityType(id): Observable<any> {
        const paramsForPost = new HttpParams().set('id', id);
        const type = 'text' as 'json';
        return this.http.post<any>(`${this.API_ENTITY_TYPE_URL}/removeEntityType`, id, { params: paramsForPost, responseType: type });
    }

    /**
     * Method that obtains a list of entities associateds to a entity type
     * @author Castor Transformación Digital - Fabio Oquendo
     * @since 2020-02-03
     * @return Observable with a json that contains a list of roles
     */
    public findEntitiesAssociated(idEntityType): Observable<any> {
        const paramsForReq = new HttpParams()
            .set('idEntityType', idEntityType);
        return this.http.get<Array<Entity>>(`${this.API_ENTITY_TYPE_URL}/findEntityAssociated`, { params: paramsForReq });
    }

    /**
     * Method that obtains a list of entity types associated to an App
     * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
     * @since 2020-03-24
     * @return Observable with a json that contains a list of entity types
     */
    public findAppEntityTypes(idApp: number): Observable<any> {
        const paramsForReq = new HttpParams()
            .set('idApplication', String(idApp));
        const noAssociated = this.http.post(this.API_APP_TO_ENTITY_TYPE_NOT_ASSOCIATED_URL, '', { params: paramsForReq });
        const associated = this.http.post(this.API_APP_TO_ENTITY_TYPE_ASSOCIATED_URL, '', { params: paramsForReq });
        return forkJoin([noAssociated, associated]);
    }

    /**
     * Associate an entity type to an app
     * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
     * @since 2020-03-24
     * @param entity type: The entity type
     * @return Observable with a json with the status of the action
     */
    public addAppEntityType(entityType: EntityType[]): Observable<any> {
        const type = 'text' as 'json';
        return this.http.post<any>(this.API_APP_TO_ENTITY_TYPE_ADD_URL, entityType, { responseType: type });
    }

    /**
     * Disassociate an entity type from an app
     * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
     * @since 2020-03-24
     * @param entity type: The entity type
     * @return Observable with a json with the status of the action
     */
    public removeAppEntityType(entityType: EntityType[]): Observable<any> {
        const type = 'text' as 'json';
        return this.http.post<any>(this.API_APP_TO_ENTITY_TYPE_REMOVE_URL, entityType, { responseType: type });
    }
}
